import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router";
import type { Stats } from "@/api/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatCount } from "@/lib/format";
import { BenchmarkMetrics, BenchmarkTable, glossary } from "@/site/benchmark";
import { PhotoTips, SectionHead, useStats } from "@/site/parts";
import { ReferenceFigure, useReferenceShark } from "@/site/reference";

// ------------------------------------------------------------------ literature

interface Reference {
  key: string;
  cite: ReactNode;
  href: string;
}

const REFERENCES: Reference[] = [
  {
    key: "Groth 1986",
    cite: (
      <>
        Groth, E. J. (1986). A pattern-matching algorithm for two-dimensional coordinate lists.{" "}
        <i>The Astronomical Journal</i> 91: 1244–1248.
      </>
    ),
    href: "https://doi.org/10.1086/114099",
  },
  {
    key: "Arzoumanian et al. 2005",
    cite: (
      <>
        Arzoumanian, Z., Holmberg, J. &amp; Norman, B. (2005). An astronomical pattern-matching
        algorithm for computer-aided identification of whale sharks <i>Rhincodon typus</i>.{" "}
        <i>Journal of Applied Ecology</i> 42: 999–1011.
      </>
    ),
    href: "https://doi.org/10.1111/j.1365-2664.2005.01117.x",
  },
  {
    key: "Van Tienhoven et al. 2007",
    cite: (
      <>
        Van Tienhoven, A. M., Den Hartog, J. E., Reijns, R. A. &amp; Peddemors, V. M. (2007). A
        computer-aided program for pattern-matching of natural marks on the spotted raggedtooth
        shark <i>Carcharias taurus</i>. <i>Journal of Applied Ecology</i> 44: 273–280.
      </>
    ),
    href: "https://doi.org/10.1111/j.1365-2664.2006.01273.x",
  },
  {
    key: "Holmberg et al. 2009",
    cite: (
      <>
        Holmberg, J., Norman, B. &amp; Arzoumanian, Z. (2009). Estimating population size,
        structure, and residency time for whale sharks <i>Rhincodon typus</i> through collaborative
        photo-identification. <i>Endangered Species Research</i> 7: 39–53.
      </>
    ),
    href: "https://doi.org/10.3354/esr00186",
  },
  {
    key: "Lang et al. 2010",
    cite: (
      <>
        Lang, D., Hogg, D. W., Mierle, K., Blanton, M. &amp; Roweis, S. (2010). Astrometry.net:
        blind astrometric calibration of arbitrary astronomical images.{" "}
        <i>The Astronomical Journal</i> 139: 1782–1800.
      </>
    ),
    href: "https://doi.org/10.1088/0004-6256/139/5/1782",
  },
  {
    key: "Carion et al. 2025",
    cite: (
      <>
        Carion, N. et al. (2025). SAM 3: Segment Anything with Concepts. <i>arXiv</i> 2511.16719.
      </>
    ),
    href: "https://arxiv.org/abs/2511.16719",
  },
];

function Cite({ refs }: { refs: string[] }) {
  return (
    <span className="label">
      {refs.map((key, i) => {
        const n = REFERENCES.findIndex((r) => r.key === key) + 1;
        return (
          <span key={key}>
            {i > 0 && " · "}
            <a href={`#ref-${n}`} className="link-u">
              [{n}] {key}
            </a>
          </span>
        );
      })}
    </span>
  );
}

// ------------------------------------------------------------------ pipeline

const PIPELINE: { name: string; text: ReactNode; refs: string[] }[] = [
  {
    name: "Segment",
    text: (
      <>
        SAM 3.1 is prompted with &ldquo;whale shark&rdquo; for the body and &ldquo;pectoral
        fin&rdquo; for the landmark. The body is cut into overlapping tiles, each enlarged and
        prompted with &ldquo;spot&rdquo;. The centres of the spots inside the body are the
        constellation.
      </>
    ),
    refs: ["Carion et al. 2025"],
  },
  {
    name: "Shortlist",
    text: (
      <>
        Every constellation in the registry is indexed by the shapes of its four-spot groups, a
        geometric hash borrowed from Astrometry.net. The closest shapes, together with the nearest
        photographs by learned embedding, make the shortlist; the leading sharks then have every
        photograph of theirs checked, so each is judged by its best likeness.
      </>
    ),
    refs: ["Lang et al. 2010"],
  },
  {
    name: "Triangles",
    text: (
      <>
        Each trio of spots forms a triangle whose side ratio and angle survive moving, turning and
        rescaling the photograph. Triangles that agree in shape vote for a common scale and
        rotation; the consensus fixes an alignment between the two constellations.
      </>
    ),
    refs: ["Groth 1986", "Arzoumanian et al. 2005"],
  },
  {
    name: "Pair",
    text: (
      <>
        With the photographs aligned, spots are paired as mutual nearest neighbours, as in I3S. Each
        pair is scored by how much likelier its offset is for the same spot than for a chance
        neighbour; the summed score is the spot evidence.
      </>
    ),
    refs: ["Van Tienhoven et al. 2007"],
  },
  {
    name: "Fuse",
    text: (
      <>
        MiewID, Wild Me&rsquo;s learned re-identification embedding, compares the two body crops as
        a whole. It carries what the geometry cannot, chiefly changes of viewpoint, and the spots
        carry what it cannot: a pattern you can check pair by pair.
      </>
    ),
    refs: [],
  },
  {
    name: "Calibrate",
    text: (
      <>
        The spot score, the number of spots in play and the embedding similarity become one
        probability that the two photographs show the same shark, fitted on the benchmark below.
        Above a high-precision threshold the site calls a match; below it, it shows candidates; with
        nothing plausible and enough spots, it offers the shark as new.
      </>
    ),
    refs: [],
  },
];

function Pipeline() {
  const reference = useReferenceShark();
  return (
    <section className="in-wrap in-section" id="pipeline" aria-labelledby="pipeline-title">
      <SectionHead index="01" title="Pipeline" id="pipeline-title">
        From one photograph to a calibrated probability, in six stages. The geometry is the
        literature&rsquo;s; the detector and the fusion are new.
      </SectionHead>
      <div className="in-grid" style={{ rowGap: 40 }}>
        <ol className="pipe" style={{ gridColumn: "span 7" }}>
          {PIPELINE.map((step, i) => (
            <li key={step.name}>
              <span className="label">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="h-3">{step.name}</h3>
                <p style={{ marginTop: 8, maxWidth: "38em" }}>{step.text}</p>
                {step.refs.length > 0 && (
                  <p style={{ marginTop: 10 }}>
                    <Cite refs={step.refs} />
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
        <div className="pipe-fig" style={{ gridColumn: "9 / span 4" }}>
          <ReferenceFigure shark={reference.data} />
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ benchmark

function Benchmark({ stats }: { stats: Stats | null }) {
  const benchmark = stats?.benchmark;
  const metrics = benchmark?.metrics ?? [];
  return (
    <section className="in-wrap in-section" id="benchmark" aria-labelledby="benchmark-title">
      <SectionHead index="02" title="Benchmark" id="benchmark-title">
        {benchmark?.description ??
          (stats ? "The benchmark has not been run against this registry yet." : "Loading.")}
      </SectionHead>
      {metrics.length > 0 && <BenchmarkMetrics metrics={metrics} />}
      {benchmark?.table && (
        <div style={{ marginTop: 48 }}>
          <BenchmarkTable table={benchmark.table} />
        </div>
      )}
      <div className="in-grid" style={{ marginTop: 20, rowGap: 12 }}>
        <p className="muted" style={{ gridColumn: "span 7", fontSize: 14 }}>
          {glossary(benchmark?.table?.columns ?? []).join(" ")}
        </p>
        {benchmark?.updated_at && (
          <p className="label" style={{ gridColumn: "9 / span 4" }}>
            Benchmark updated {benchmark.updated_at}
          </p>
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ limits & photographing

/** Plan view: the shark from above, the camera's line of sight, and how far off side-on works. */
function ViewpointFigure() {
  const cx = 400;
  const cy = 122;
  const R = 220;
  const ray = (deg: number, r = R) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + Math.sin(a) * r, y: cy + Math.cos(a) * r };
  };
  const wedge = (from: number, to: number) => {
    const a = ray(from);
    const b = ray(to);
    return `M${cx},${cy} L${a.x},${a.y} A${R},${R} 0 0 ${from < to ? 0 : 1} ${b.x},${b.y} Z`;
  };
  const bands: [number, number, string, number][] = [
    [-10, 10, "var(--signal)", 0.3],
    [10, 20, "var(--signal)", 0.1],
    [-20, -10, "var(--signal)", 0.1],
  ];
  return (
    <svg
      viewBox="0 0 600 380"
      role="img"
      aria-label="Plan view: spot matching works within about 10 degrees of side-on, degrades to 20 degrees, and fails beyond"
    >
      <rect width="600" height="380" fill="var(--panel)" />
      {bands.map(([from, to, fill, opacity]) => (
        <path key={from} d={wedge(from, to)} fill={fill} opacity={opacity} />
      ))}
      <g stroke="var(--ink-3)" strokeWidth="1">
        {[-40, -30, -20, -10, 0, 10, 20, 30, 40].map((d) => {
          const end = ray(d);
          return (
            <line
              key={d}
              x1={cx}
              y1={cy}
              x2={end.x}
              y2={end.y}
              strokeDasharray={d === 0 ? undefined : "4 6"}
              stroke={d === 0 ? "var(--ink)" : undefined}
              strokeWidth={d === 0 ? 1.5 : 1}
            />
          );
        })}
      </g>
      <g fontFamily="var(--mono)" fontSize="13" fill="var(--ink-2)" textAnchor="middle">
        {[-40, -30, -20, -10, 0, 10, 20, 30, 40].map((d) => {
          const p = ray(d, R + 18);
          return (
            <text key={d} x={p.x} y={p.y + 4}>
              {Math.abs(d)}°
            </text>
          );
        })}
      </g>
      <g fill="var(--ink)" aria-hidden="true">
        <path d="M530 96 C530 78 516 71 490 70 C420 67 330 74 250 84 C210 89 180 92 150 94 L150 98 C180 100 210 103 250 108 C330 118 420 125 490 122 C516 121 530 114 530 96 Z" />
        <path d="M152 94 L84 88 L92 96 L84 104 L152 98 Z" />
        <path d="M440 71 L392 30 L406 72 Z M440 121 L392 162 L406 120 Z" />
      </g>
      <path
        d="M366 120 Q400 123 436 122"
        fill="none"
        stroke="var(--signal)"
        strokeWidth="6"
        strokeLinecap="square"
      />
    </svg>
  );
}

function Limits() {
  return (
    <section className="in-wrap in-section" id="limits" aria-labelledby="limits-title">
      <SectionHead index="03" title="Limits" id="limits-title">
        Spot matching needs a roughly side-on photograph of the flank. That is why the site does not
        rely on it alone.
      </SectionHead>
      <div className="in-grid" style={{ rowGap: 32 }}>
        <div style={{ gridColumn: "span 6", display: "grid", gap: 16, alignContent: "start" }}>
          <p>
            The spots sit on a curved, flexing body. Turn the camera away from side-on and each spot
            shifts by an amount that depends on where it sits on the curve, so the constellation
            bows in a way no single alignment can undo. Past about 10&ndash;20&deg; off side-on,
            spots alone stop finding the shark. In a controlled simulation (40 synthetic sharks, 12
            queries per angle) the right shark came first for every side-on query, two in three at
            10&deg; of yaw and none at 20&deg;.
          </p>
          <p>
            On real photographs the same thing shows: where the query and the registry&rsquo;s best
            photograph differ most in viewpoint, spot matching finds the right shark about half as
            often as where they agree, while MiewID, which learned viewpoint from data, barely
            moves. So the site fuses the two, and shows you the spot evidence whenever there is some
            to show.
          </p>
          <p className="note">
            Left and right flanks carry unrelated patterns, and a photograph from above shows the
            back, which neither flank covers. The site reads each photograph&rsquo;s view and
            compares its spots only with photographs of the same view; across views only the
            embedding can link a shark. A photograph from above is never offered for naming, since
            few sharks have one on record to rule out.
          </p>
        </div>
        <figure style={{ gridColumn: "8 / span 5", margin: 0 }}>
          <div className="fig-head">
            <span className="label">Fig. B · Viewpoint, plan view</span>
            <span className="label">Schematic</span>
          </div>
          <div className="plate plate-chart">
            <ViewpointFigure />
          </div>
          <figcaption className="fig-cap" style={{ flexWrap: "wrap" }}>
            <span className="label">
              <i className="swatch" data-strength="strong" /> ≤ 10° reliable
            </span>
            <span className="label">
              <i className="swatch" data-strength="weak" /> 10–20° degrading
            </span>
            <span className="label">
              <i className="swatch" /> &gt; 20° spots alone fail
            </span>
            <span className="label">
              <i className="swatch" data-strength="line" /> Reference area
            </span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function Photographing() {
  return (
    <section className="in-wrap in-section" id="photographing" aria-labelledby="photo-title">
      <SectionHead index="04" title="Taking a readable photograph" id="photo-title">
        The pattern is read from the flank behind the gills and above the pectoral fin, the
        reference area the photo-ID literature standardised on.
      </SectionHead>
      <div className="in-grid" style={{ rowGap: 24 }}>
        <div style={{ gridColumn: "span 7" }}>
          <PhotoTips />
        </div>
        <p className="muted" style={{ gridColumn: "9 / span 4", fontSize: 15 }}>
          One sharp, side-on frame beats a burst of oblique ones. If the result comes back without a
          readable pattern, the page says why; nothing is stored against a shark until you confirm
          it.
        </p>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ sources

const SOURCES: { key: string; name: string; href: string; what: string; licence: string }[] = [
  {
    key: "lila",
    name: "LILA BC · Whale Shark ID (Wild Me)",
    href: "https://lila.science/datasets/whale-shark-id",
    what: "543 Ningaloo sharks, photographs with individual labels. The backbone of the benchmark.",
    licence: "CDLA-Permissive-1.0",
  },
  {
    key: "sharkbook",
    name: "whaleshark.org export (Sharkbook)",
    href: "https://www.sharkbook.ai",
    what: "1,541 sharks from about 20 regional catalogues, as hand-marked spot positions. The photographs stay on Sharkbook, linked from each record.",
    licence: "Spots only; photographs not copied",
  },
  {
    key: "inaturalist",
    name: "iNaturalist",
    href: "https://www.inaturalist.org/taxa/52188-Rhincodon-typus",
    what: "Research-grade observations, identified the same way an upload is.",
    licence: "CC0 / CC BY / CC BY-NC, per photo, credited",
  },
  {
    key: "gbif",
    name: "GBIF",
    href: "https://www.gbif.org/species/2417522",
    what: "Occurrence media not already mirrored from iNaturalist.",
    licence: "CC, per item, credited",
  },
  {
    key: "wikimedia",
    name: "Wikimedia Commons",
    href: "https://commons.wikimedia.org/wiki/Category:Rhincodon_typus",
    what: "Openly licensed photographs, identified the same way an upload is.",
    licence: "CC / public domain, credited",
  },
];

function Sources({ stats }: { stats: Stats | null }) {
  return (
    <section className="in-wrap in-section" id="sources" aria-labelledby="sources-title">
      <SectionHead index="05" title="Data sources and licences" id="sources-title">
        Every photograph stays its photographer&rsquo;s. Each is shown with its credit, its licence
        and a link to where it was published, and only openly licensed photographs are shown.
      </SectionHead>
      <div className="scroll-x">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col" className="hide-sm">
                Contribution
              </th>
              <th scope="col">Licence</th>
              <th scope="col" className="r">
                Records
              </th>
            </tr>
          </thead>
          <tbody>
            {SOURCES.map((s) => {
              const n = stats?.sources[s.key];
              return (
                <tr key={s.key}>
                  <th scope="row" style={{ fontWeight: 500 }}>
                    <a href={s.href} className="link-u" target="_blank" rel="noreferrer">
                      {s.name}
                    </a>
                  </th>
                  <td className="muted hide-sm" style={{ maxWidth: "34em" }}>
                    {s.what}
                  </td>
                  <td className="label">{s.licence}</td>
                  <td className="r mono">{n === undefined ? "—" : formatCount(n)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 20, fontSize: 14, maxWidth: "46em" }}>
        Thank you to the divers, researchers and citizen scientists whose photographs make up the
        registry, and to Wild Me and Sharkbook, whose whale shark work made this kind of
        identification possible.
      </p>
    </section>
  );
}

function Literature() {
  return (
    <section className="in-wrap in-section" id="literature" aria-labelledby="literature-title">
      <SectionHead index="06" title="Literature" id="literature-title">
        The method in its sources, oldest first.
      </SectionHead>
      <ol className="refs">
        {REFERENCES.map((ref, i) => (
          <li key={ref.key} id={`ref-${i + 1}`}>
            <span className="label">[{i + 1}]</span>
            <p>
              {ref.cite}{" "}
              <a
                href={ref.href}
                className="link-u mono"
                style={{ fontSize: 13, wordBreak: "break-all" }}
                target="_blank"
                rel="noreferrer"
              >
                {ref.href.replace(/^https:\/\//, "")}
              </a>
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ------------------------------------------------------------------ page

const CONTENTS: [string, string][] = [
  ["pipeline", "Pipeline"],
  ["benchmark", "Benchmark"],
  ["limits", "Limits"],
  ["photographing", "Photographing"],
  ["sources", "Sources and licences"],
  ["literature", "Literature"],
];

export function AboutPage() {
  useDocumentMeta({
    title: "Method",
    description:
      "How Gillspotter identifies whale sharks: SAM 3.1 spots, Groth triangle matching, I3S-style " +
      "pairing, fusion with MiewID and a calibrated probability, with the benchmark behind it.",
  });
  const stats = useStats();
  const { hash } = useLocation();
  const loaded = stats !== null;
  // The benchmark pushes later sections down when it arrives: land on the linked one again.
  useEffect(() => {
    if (loaded && hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [loaded, hash]);
  return (
    <>
      <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 32 }}>
        <div style={{ gridColumn: "span 8" }} className="anim-in">
          <p className="label">
            <span className="status-dot" /> Method · how the instrument works
          </p>
          <h1 className="display-2" style={{ marginTop: 14 }}>
            Spots read as stars, matched as constellations.
          </h1>
          <p className="lede" style={{ marginTop: 20 }}>
            In 2005 an astrophysicist, a software engineer and a marine biologist noticed that a
            whale shark&rsquo;s spots look like a star field, and that astronomers had already
            solved recognising one patch of sky in another photograph. Their matcher became the
            backbone of global whale shark photo-identification. Gillspotter runs the same geometry
            on spots found by a segmentation model, and checks it against a learned embedding.
          </p>
        </div>
        <nav
          aria-label="On this page"
          style={{ gridColumn: "9 / span 4", alignSelf: "end" }}
          className="anim-in"
        >
          <ol className="toc">
            {CONTENTS.map(([id, title], i) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span className="label">{String(i + 1).padStart(2, "0")}</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </section>
      <Pipeline />
      <Benchmark stats={stats} />
      <Limits />
      <Photographing />
      <Sources stats={stats} />
      <Literature />
    </>
  );
}
